import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarConfig, MatSnackBarModule } from '@angular/material/snack-bar';
import { ProjectService } from '../../../../../../common/services/project.service';
import { SearchService } from '../../../../../../common/services/search.service';
import { CustomizerSettingsService } from '../../../../../../customizer-settings/customizer-settings.service';
import { GenericDialogComponent } from '../../../../../../common/components/dialog/dialog';
import { RefundSummary } from '../../../../../../common/models/project';
import { Subject, takeUntil } from 'rxjs';


@Component({
    selector: 'app-refunds',
    templateUrl: './refunds.component.html',
    styleUrl: './refunds.component.scss',
    imports: [MatCardModule,
        MatMenuModule,
        MatButtonModule,
        MatTableModule,
        MatPaginatorModule,
        MatTooltipModule,
        MatIconModule,
        MatDialogModule,
        MatSnackBarModule,
        CommonModule
    ]
})
export class RefundsComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();

    projectId: string = '';

    refunds: RefundSummary[] = [];
    filteredRefunds: RefundSummary[] = [];

    isToggled = false;

    private readonly snackbarConfig: MatSnackBarConfig = {
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top'
    };
  
    constructor(
      private router: Router,
      private projectService: ProjectService,
      private searchService: SearchService,
      public themeService: CustomizerSettingsService,
      private dialog: MatDialog,
      private snackBar: MatSnackBar,
      private route: ActivatedRoute
    ) {}
  
    ngOnInit() {
      this.themeService.isToggled$.pipe(takeUntil(this.destroy$)).subscribe(isToggled => {
        this.isToggled = isToggled;
      });
      this.searchService.currentSearchTerm$.pipe(takeUntil(this.destroy$)).subscribe(term => {
        this.applySearch(term);
      });

      this.route.paramMap.subscribe(params => {
          this.projectId = params.get('projectId') ?? '';
          this.fetchRefunds();
      });
  
    }

    ngOnDestroy() {
      this.destroy$.next();
      this.destroy$.complete();
    }
  
    fetchRefunds(): void {
      this.projectService.getRefunds(this.projectId).pipe(takeUntil(this.destroy$)).subscribe({
        next: (data) => {
          this.refunds = data;
          this.filteredRefunds = data;
        },
        error: (error) => {
          console.error('Error fetching refunds', error);
        }
      });
    }

    applySearch(searchTerm: string): void {
      searchTerm = searchTerm.trim().toLowerCase();
      if (searchTerm) {
        this.filteredRefunds = this.refunds.filter((refund: RefundSummary) => {
          return (
             refund.classificationCode.toLowerCase().includes(searchTerm) ||
             refund.referenceNumber.toLowerCase().includes(searchTerm)
          );
        });
      } else {
        this.filteredRefunds = [...this.refunds];
      }
    }

    confirmDelete(contract: RefundSummary): void {
      const dialogRef = this.dialog.open(GenericDialogComponent, {
        width: '50%',
        data: {
          title: 'Potvrdite brisanje',
          message: `Jeste li sigurni da želite obrisati Odluku o povratu?`,
          confirmButtonText: 'Da',
          cancelButtonText: 'Ne',
        },
      });
  
      dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
        if (result === true) {
          this.deleteRefund(contract.id);
        }
      });
    }
  
    deleteRefund(refundId: string): void {
      this.projectService.deleteRefund(this.projectId, refundId).pipe(takeUntil(this.destroy$)).subscribe(
        () => {
          this.fetchRefunds();
          this.snackBar.open('Odluka o povratu je obrisana', 'Zatvori', this.snackbarConfig);
        },
        (error) => {
          console.error('Error deleting refund', error);
        }
      );
    }

    downloadRefund(refund: RefundSummary): void {
      this.projectService.downloadRefund(this.projectId, refund.id).pipe(takeUntil(this.destroy$)).subscribe({
        next: (blob) => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'Odluka o povratu sredstava.docx';
          a.click();
          window.URL.revokeObjectURL(url);
        },
        error: (err) => {
          console.error('Error downloading refund', err);
        }
      });
    }

    goToCreateRefund(): void {
      this.router.navigate(['municipality', 'projects', this.projectId, 'refunds', 'create' ]);
    }

    goToViewRefund(refund: RefundSummary): void {
      this.router.navigate(['municipality', 'projects', this.projectId, 'refunds', refund.id,  'view' ]);
    }
  
    goToEditRefund(refund: RefundSummary): void {
      this.router.navigate(['municipality', 'projects', this.projectId, 'refunds', refund.id,  'edit' ]);
    }
  }
